import { TrendingUp } from "lucide-react/dist/cjs/lucide-react";
import React, { useMemo } from "react";
import { formatter } from "../../utils/utils";
import { FaCaretUp } from "react-icons/fa";

// Tailwind-only implementation of the Pricing Guidance card.
// Usage:
/* <PricingGuidanceCard band={{
  confidence: "High",
  bandLow: 90000,
  bandHigh: 120000,
  ask: 110000,
  suggested: 100000,
  bestComp: 98000,
  direction: "down",
  position: "Inside",
  ppcPredicted: 102000,
  ppcPredictedFrom: 98500,
  ppcPredictedTo: 105500,
  step: 500,
  daysOnHand: 45,
  p75Days: 60,
  sellProbability30d: 72,
  reasons: ["low_demand", "high_carat", "market_trend"]
}} /> */

const resolveValue = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined && candidate !== "") {
      return candidate;
    }
  }
  return null;
};

const sanitizeNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  if (cleaned.trim() === "") return null;

  const numeric = Number(cleaned);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const numeric = sanitizeNumber(value);
  if (numeric === null) return String(value);
  return numeric.toLocaleString("en-IN");
};

const confidenceBadgeClass = (confidence) => {
  if (!confidence || typeof confidence !== "string") {
    return "bg-gray-100 text-gray-700";
  }

  const normalized = confidence.toLowerCase();

  if (normalized.startsWith("high")) {
    return "bg-green-100 text-green-800 border-green-300";
  }

  if (normalized.startsWith("med")) {
    return "bg-amber-100 text-amber-800 border-amber-300";
  }

  if (normalized.startsWith("low")) {
    return "bg-red-100 text-red-800 border-red-300";
  }

  return "bg-gray-100 text-gray-700 border-gray-300";
};


const PricingGuidanceCard = ({ band }) => {
  const data = useMemo(() => {
    if (!band) return null;
    return {
      confidence: resolveValue(band.confidence),
      bandLow: resolveValue(band.bandLow),
      bandHigh: resolveValue(band.bandHigh),
      ask: resolveValue(band.ask),
      suggested: resolveValue(band.suggested),
      bestComp: resolveValue(
        band.bestComp,
      ),
      direction: resolveValue(band.direction),
      position: resolveValue(band.position),
      ppcPredicted: resolveValue(
        band.ppcPredicted,
        
      ),
      ppcPredictedFrom: resolveValue(
        band.ppcPredictedFrom,
        
      ),
      ppcPredictedTo: resolveValue(
        band.ppcPredictedTo,
        
      ),
      step: resolveValue(band.step, band.step_value),
      daysOnHand: resolveValue(band.daysOnHand, band.days_on_hand),
      p75Days: resolveValue(band.p75Days, band.p75_days),
      sellProbability30d: resolveValue(
        band.sellProbability30d,
        band.sell_probability_30d
      ),
      reasons: Array.isArray(band.reasons) ? band.reasons : [],
    };
  }, [band]);

  if (!data) return null;

  const numericBandLow = sanitizeNumber(data.bandLow);
  const numericBandHigh = sanitizeNumber(data.bandHigh);
  const numericAsk = sanitizeNumber(data.ask);
  const numericSuggested = sanitizeNumber(data.suggested);
  const numericBestComp = sanitizeNumber(data.bestComp);
  const numericPpcPredicted = sanitizeNumber(data.ppcPredicted);
  const numericPredictedFrom = sanitizeNumber(data.ppcPredictedFrom);
  const numericPredictedTo = sanitizeNumber(data.ppcPredictedTo);

  const diff =
    numericBandLow !== null &&
    numericBandHigh !== null &&
    numericBandHigh >= numericBandLow
      ? numericBandHigh - numericBandLow
      : null;

  const plottedValues = [
    numericBandLow,
    numericBandHigh,
    numericAsk,
    numericSuggested,
    numericBestComp,
  ].filter((val) => val !== null);

  const globalMin = plottedValues.length ? Math.min(...plottedValues) : null;
  const globalMax = plottedValues.length ? Math.max(...plottedValues) : null;
  const hasGlobalRange = globalMin !== null && globalMax !== null && globalMax > globalMin;

  const relativePosition = (value) => {
    if (value === null || globalMin === null || globalMax === null) return null;
    if (!hasGlobalRange) return 0.5;

    const fraction = (value - globalMin) / (globalMax - globalMin);
    return Math.max(0, Math.min(1, fraction));
  };

  const posWithinRange = (value) => {
    const relative = relativePosition(value);
    const fallback = hasGlobalRange ? 0 : 0.5;
    return `${((relative ?? fallback) * 100).toFixed(2)}%`;
  };

  const bandStart = relativePosition(numericBandLow);
  const bandEnd = relativePosition(numericBandHigh);
  const bandLeft = `${((bandStart ?? 0) * 100).toFixed(2)}%`;
  const bandWidth =
    bandStart !== null && bandEnd !== null
      ? `${(Math.max(bandEnd - bandStart, 0) * 100).toFixed(2)}%`
      : "100%";

  const insideBand =
    data.position !== null &&
    numericBandLow !== null &&
    numericBandHigh !== null &&
    data.position == 0;

  const midPoint =
    numericBandLow !== null && numericBandHigh !== null
      ? (numericBandLow + numericBandHigh) / 2
      : null;

  const pctAway =
    midPoint && numericSuggested !== null && midPoint !== 0
      ? Math.abs(((numericSuggested - midPoint) / midPoint) * 100)
      : null;

  const metrics = [
    // numericBandLow !== null && numericBandHigh !== null
    //   ? {
    //       label: "Band Range",
    //       value: `$${formatNumber(numericBandLow)}–₹${formatNumber(numericBandHigh)}`,
    //     }
    //   : null,
    data.suggested !== null
      ? {
          label: "Suggested PPC",
          value: data.suggested ? `${formatter.format(Math.round(data.suggested))}`: "-",
          sub: data.step ? `Step ${formatter.format(Math.round(data.step))}` : null,
          align: "start",
        }
      : null,
    // data.ask !== null
    //   ? {
    //       label: "PPC Predicted",
    //       value: `$${formatNumber(data.ask)} /ct`,
    //     }
    //   : null,
    data.bestComp !== null
      ? {
          label: "Best Comp PPC",
          value: data.bestComp ? `${formatter.format(Math.round(data.bestComp))}`: "-",
          align: "end",
        }
      : null,
    // data.direction
    //   ? {
    //       label: "Direction",
    //       value:
    //         typeof data.direction === "string"
    //           ? data.direction.replace(/_/g, " ")
    //           : String(data.direction),
    //     }
    //   : null,
    // data.position !== null
    //   ? {
    //       label: "Position",
    //       value: String(data.position),
    //     }
    //   : null,
  ].filter(Boolean);

  // const rangeMeta =
  //   numericPredictedFrom !== null || numericPredictedTo !== null
  //     ? `$${numericPredictedFrom !== null ? formatNumber(numericPredictedFrom) : "-"}–₹${
  //         numericPredictedTo !== null ? formatNumber(numericPredictedTo) : "-"
  //       }`
  //     : null;

  // const dohText =
  //   data.daysOnHand !== null && data.p75Days !== null
  //     ? (() => {
  //         const over =
  //           sanitizeNumber(data.daysOnHand) !== null &&
  //           sanitizeNumber(data.p75Days) !== null &&
  //           sanitizeNumber(data.daysOnHand) > sanitizeNumber(data.p75Days);

  //         if (!over) return "bg-green-50 text-green-800 border-green-300";

  //         const numericDays = sanitizeNumber(data.daysOnHand);
  //         const numericP75 = sanitizeNumber(data.p75Days);
  //         const ratio =
  //           numericDays !== null && numericP75
  //             ? (numericDays - numericP75) / (numericP75 || 1)
  //             : 0;

  //         if (ratio <= 0.15) return "bg-amber-50 text-amber-800 border-amber-300";
  //         return "bg-red-50 text-red-800 border-red-300";
  //       })()
  //     : "bg-gray-50 text-gray-700 border-gray-200";

  return (
    <div className="w-full px-1">
        <div className="py-3 px-1 pl-1 border-b w-full">
          <div className="flex items-center justify-between pr-3">
            <div className="text-sm flex items-center gap-2 font-semibold text-gray-900">
              
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <span>Pricing Guidance</span>
            </div>
            <p
              className={`inline-flex items-center rounded px-2 py-1 text-[0.6rem] font-medium ${confidenceBadgeClass(
                data.confidence
              )}`}
            >
              {data.confidence || "—"}
            </p>
          </div>
        </div>

        <div className="pt-2 px-4 w-full pb-2 space-y-2">
          {numericBandLow !== null && numericBandHigh !== null && diff !== null && (
            <div className="space-y-2">
              <div className="relative h-8 w-full border border-gray-100/70 bg-gray-100/60 rounded">
                <div
                  className="absolute top-1/2 -translate-y-1/2 h-2 bg-blue-200 rounded"
                  style={{ left: bandLeft, width: bandWidth }}
                />

                {numericAsk !== null && (
                  <div
                    className="absolute z-10 top-1/2 -translate-y-1/2 -translate-x-1/2"
                    style={{ left: posWithinRange(numericAsk) }}
                  >
                    <FaCaretUp className="w-3 h-6 mt-1 text-amber-600" />
                    <div className="absolute  top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-amber-700 font-medium">
                      Ask
                    </div>
                  </div>
                )}

                {(numericSuggested !== null && numericSuggested !== 0) && (
                  <div
                    className="absolute z-10 top-1/2 -translate-y-1/2 -translate-x-1/2"
                    style={{ left: posWithinRange(numericSuggested) }}
                  >
                    <div className="w-3 h-3 rounded-full bg-blue-600 border border-2 border-white" />
                    <div className="absolute top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-blue-700 font-medium">
                      Suggested
                    </div>
                  </div>
                )}

                {numericBestComp !== null && (
                  <div
                    className="absolute top-1 z-10 bottom-0 w-0.5 h-6 bg-gray-600"
                    style={{ left: posWithinRange(numericBestComp) }}
                  >
                    <div className="absolute bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-gray-700 font-medium">
                      Comp
                    </div>
                  </div>
                )}

                {numericBestComp !== null && (
                  <div
                    className="absolute z-0  top-[0.93rem] bottom-0 w-full h-[0.05rem] bg-blue-400"
                    style={{ left: 0 }}
                  >
                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-gray-700 font-medium">
                      
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between text-[0.69rem] mt-4 text-gray-500">
                <span>
                  Band Low
                  <br />
                  <span className="font-semibold text-gray-900">
                    {data.bandLow ? formatter.format(Math.round(data.bandLow)) : "-"}
                  </span>
                </span>
                <span className="text-right">
                  Band High
                  <br />
                  <span className="font-semibold text-gray-900">
                    {data.bandHigh ? formatter.format(Math.round(data.bandHigh)): "-"}
                  </span>
                </span>
              </div>
            </div>
          )}


          {metrics.length > 0 && (
            <div className=" grid grid-cols-2 gap-3 text-[0.69rem]">
              {metrics.map(({ label, value, sub, align }) => (
                <div className={`flex flex-col justify-between items-${align}`} key={label}>
                  <span className="text-gray-500">{label}</span>
                  <div className="font-semibold text-gray-900">{value}</div>
                  {/* {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>} */}
                </div>
              ))}
            </div>
          )}

          {/* {(rangeMeta || data.sellProbability30d !== null) && (
            <div className="grid grid-cols-1 gap-3 text-sm">
              {rangeMeta && (
                <div>
                  <span className="text-gray-500">Predicted Range</span>
                  <div className="font-semibold text-gray-900">{rangeMeta}</div>
                </div>
              )}
              {data.sellProbability30d !== null && (
                <div>
                  <span className="text-gray-500">30-day sell probability</span>
                  <div className="font-semibold text-gray-900">
                    {formatNumber(data.sellProbability30d)}%
                  </div>
                </div>
              )}
            </div>
          )} */}

          {/* {(data.daysOnHand !== null || data.p75Days !== null) && (
            <div
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border font-medium text-sm w-fit ${dohText}`}
            >
              {data.daysOnHand !== null && (
                <span className="font-semibold">{formatNumber(data.daysOnHand)}d</span>
              )}
              {data.p75Days !== null && (
                <span className="text-gray-500">(P75: {formatNumber(data.p75Days)}d)</span>
              )}
            </div>
          )} */}

          {data.position !== null && (
            <div className="px-2 flex justify-start items-center py-1 rounded  bg-gray-100/50 text-[0.69rem] text-gray-700">
              <p className="text-gray-500">Position:&nbsp;</p>
              <p className="font-semibold text-gray-900">
                {insideBand
                  ? "Inside band"
                  : data.position >  0
                  ? "Above band"
                  : data.position < 0
                  ? "Below band"
                  : "—"}
                {pctAway !== null && !insideBand ? ` by ${Math.round(Math.abs(data.position))}%` : ""}
              </p>
            </div>
          )}

          {/* {Array.isArray(data.reasons) && data.reasons.length > 0 && (
            <div>
              <span className="text-[0.69rem] text-gray-500">Reasons</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {data.reasons.slice(0, 4).map((reason) => (
                  <span
                    key={reason}
                    className="text-[0.69rem] font-normal border border-gray-300 text-gray-700 px-2  rounded"
                  >
                    {String(reason).replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            </div>
          )} */}
        </div>
    </div>
  );
};

export default PricingGuidanceCard;
